import { APP_EVENTS } from "@/domain/app-events";
import { useEffect } from "react";
import { useUpdateUserPreferencesByID } from "../user/user-preferences.mutations";
import { z } from "zod";
import { ServiceLogger } from "@/application/services/logger";

const DetailSchema = z.object({
  userExtraPreferences: z.record(z.any()),
  userPreferencesID: z.string().min(1),
});

export function UseOnboardingCompletedListener() {
  const { mutate: updatePreferences } = useUpdateUserPreferencesByID();

  useEffect(() => {
    async function handleOnboardingCompleted(event: Event) {
      ServiceLogger.info(
        `${APP_EVENTS.ONBOARDING_TOUR_COMPLETED} event caught`
      );

      if (event === null || !(event instanceof CustomEvent)) {
        ServiceLogger.error(
          `${APP_EVENTS.ONBOARDING_TOUR_COMPLETED}: invalid event type`
        );
        return;
      }

      const { data, error } = DetailSchema.safeParse(event?.detail);

      if (error || !data) {
        ServiceLogger.error(
          `${APP_EVENTS.ONBOARDING_TOUR_COMPLETED}: invalid event detail`,
          error
        );
        return;
      }

      updatePreferences(
        {
          ID: data.userPreferencesID,
          preferences: {
            extra: JSON.stringify({
              ...data.userExtraPreferences,
              onboarding_tour_watched: true,
            }),
          },
        },
        {
          onError: (error) => {
            ServiceLogger.error(
              `${APP_EVENTS.ONBOARDING_TOUR_COMPLETED}: failed to update user preferences`,
              error
            );
          },
          onSuccess: () => {
            ServiceLogger.info(
              `${APP_EVENTS.ONBOARDING_TOUR_COMPLETED}: user preferences updated successfully`
            );
          },
        }
      );
    }

    // Register the event listener
    document.addEventListener(
      APP_EVENTS.ONBOARDING_TOUR_COMPLETED,
      handleOnboardingCompleted
    );
    return () => {
      document.removeEventListener(
        APP_EVENTS.ONBOARDING_TOUR_COMPLETED,
        handleOnboardingCompleted
      );
    };
  }, [updatePreferences]);
}
